import { Table, TextInput, Button, Pagination, Select, Label, ButtonGroup, Tooltip, Datepicker } from "flowbite-react";
import { HiSearch, HiPlus, HiPencil, HiTrash, HiBell } from "react-icons/hi";
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef } from "react";
import useNotes from "../hooks/useNotes";
import { useReducerAsState } from "../hooks/useReducerAsState";
import { NoteFilters } from "../models/notes.model.";
import { routes } from "../routes";
import useCategories from "../hooks/useCategories";
import { formatDate } from "../utils/formatters";
import { toast } from "react-toastify";
import { TableResults } from "../components";
import { useAuth } from "../context/authContext";

interface State {
    categoryId: number;
    published: number;
}

type StateMergeFilers = State & NoteFilters

const NotesPage = () => {
    const { permissions } = useAuth()

    const canCreate = permissions.find(item => item.slug === 'crear-notas')
    const canDelete = permissions.find(item => item.slug === 'borrar-notas')
    const canEdit = permissions.find(item => item.slug === 'editar-notas')
    const canNotify = permissions.find(item => item.slug === 'notificar-nota');

    const keywordsRef = useRef<HTMLInputElement>(null)
    const categoryRef = useRef<HTMLSelectElement>(null)
    const publishedRef = useRef<HTMLSelectElement>(null)

    const [state, setState] = useReducerAsState<StateMergeFilers>({
        createdAt: null,
        keywords: "",
        categoryId: -1,
        published: 1,
        limit: 10,
        page: 1
    })

    const { notes, total, loading, error, fetchNotes, deleteNote, sendNotification } = useNotes();
    const { categories, fetchCategories } = useCategories()

    useEffect(() => {
        fetchCategories()
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        fetchNotes({ ...state })
        // eslint-disable-next-line
    },[state.page])

    const navigate = useNavigate();

    const handleCreateNote = () => navigate(`${routes.DASHBOARD}/${routes.NOTES}/create`)

    const onDeleteNote = async (noteId: number) => {
        if(!noteId) return

        const confirm = window.confirm('¿Deseas eliminar esta nota? Esta acción no se puede deshacer.')
        if(!confirm) return

       const response = await deleteNote(noteId)
       toast(response ? "Nota eliminada correctamente" : "Ocurrió un error al eliminar la nota.", {
        type: response ? "success" : "error"
       })

       await fetchNotes({ ...state })
    }

    const handleCalendarChange = (e:any) => setState({createdAt: e})

    const onNotifyNote = async (noteId: number) => {
        if(!noteId) return
        if(!canNotify) {
            toast("No tienes permiso para notificar notas",{
                type: "warning"
            })
            return
        }

        const sent = await sendNotification(noteId)
        toast(sent ? "Notificación enviada correctamente" : "Error al enviar notificación",{
            type : sent ? "success" : "error"
        })
    }

    const onPageChange = (page: number) => setState({
        ...state,
        page,
        createdAt: state.createdAt !== null ? new Date(state.createdAt!).toISOString() : null,
    });

    const onFilter = () =>{
        const categoryId = categoryRef.current ? parseInt(categoryRef.current.value) :  -1
        const published = publishedRef.current ? parseInt(publishedRef.current.value) : -1
        const keywords = keywordsRef.current?.value || ""
        setState({keywords, categoryId, published})
        fetchNotes({
            keywords,
            categoryId,
            published,
            createdAt: state.createdAt !== null ? new Date(state.createdAt!).toISOString() : null
        })
    }

    return <div className="m-4">
        <div className="flex flex-col mb-4">
            <div className="mb-4">
                <h1>Noticias</h1>
            </div>
            <div className="flex flex-row justify-between items-end">
                <div className="flex flex-row justify-between items-end space-x-4">
                    <div className="max-w-md">
                        <div className="mb-2 block">
                            <Label value="Fecha" />
                        </div>
                        <Datepicker 
                            language="es-ES" 
                            labelTodayButton="Hoy"
                            labelClearButton="Limpiar"
                            onSelectedDateChanged={handleCalendarChange}
                        />
                    </div>
                    <div className="max-w-md">
                        <div className="mb-2 block">
                            <Label value="" />
                        </div>
                        <TextInput 
                            placeholder="Palabras clave"
                            icon={HiSearch}
                            ref={keywordsRef}
                        />
                    </div>
                    <div className="max-w-md">
                        <div className="mb-2 block">
                            <Label htmlFor="categories" value="Categoría" />
                        </div>
                        <Select ref={categoryRef} id="categories" required>
                            <option value="-1">- Elija una opción -</option>
                            {categories.length && categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                        </Select>
                    </div>
                    <div className="max-w-md">
                        <div className="mb-2 block">
                            <Label htmlFor="published" value="Publicado" />
                        </div>
                        <Select id="published" required ref={publishedRef}>
                            <option value="1">Si</option>
                            <option value="0">No</option>
                        </Select>
                    </div>
                    <div className="mb-0">
                        <Button onClick={onFilter}>Filtrar</Button>
                    </div>
                </div>
                {canCreate && <div className="flex flex-row">
                    <Button onClick={handleCreateNote}>
                        <HiPlus className="mr-2 h-5 w-5" />
                        Crear noticia
                    </Button>
                </div>}
            </div>
        </div>
        <TableResults
            loading={loading}
            error={error}
            itemsCount={total}
        >
            <div className="overflow-x-auto mb-4 w-full bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700">
                <Table hoverable>
                    <Table.Head>
                        <Table.HeadCell>Título</Table.HeadCell>
                        <Table.HeadCell>Desc. corta</Table.HeadCell>
                        <Table.HeadCell>Categoría</Table.HeadCell>
                        <Table.HeadCell>Publicado</Table.HeadCell>
                        <Table.HeadCell>Creado</Table.HeadCell>
                        <Table.HeadCell>
                            <span className="sr-only">Editar</span>
                        </Table.HeadCell>
                    </Table.Head>
                    <Table.Body className="divide-y">
                        {notes.map((note) => <Table.Row className="bg-white dark:border-gray-700 dark:bg-gray-800" key={note.id}>
                            <Table.Cell className="font-medium text-gray-900 dark:text-white">
                                {note.title}
                            </Table.Cell>
                            <Table.Cell>{note.excerpt}</Table.Cell>
                            <Table.Cell>{note.categories.map((item) => item.name).join(", ")}</Table.Cell>
                            <Table.Cell>{note.published === 1 ? 'Si':'No'}</Table.Cell>
                            <Table.Cell>{formatDate(note.created_at!)}</Table.Cell>
                            <Table.Cell>
                                <ButtonGroup>
                                    {canEdit && <Button color="gray" onClick={() => navigate(`${routes.DASHBOARD}/${routes.NOTES}/${note.id}`)}>
                                        <Tooltip content="Editar">
                                            <HiPencil />
                                        </Tooltip>
                                    </Button>}
                                    {canDelete && <Button color="gray" onClick={() => onDeleteNote(note.id!)}>
                                        <Tooltip content="Eliminar">
                                            <HiTrash />
                                        </Tooltip>
                                    </Button>}
                                    {canNotify && <Button onClick={() => onNotifyNote(note.id!)} color="gray">
                                        <Tooltip content="Enviar notificación">
                                            <HiBell />
                                        </Tooltip>
                                    </Button>}
                                </ButtonGroup>
                            </Table.Cell>
                        </Table.Row>)}
                    </Table.Body>
                </Table>
            </div>
        </TableResults>
        {notes.length > 0 && <div className="flex overflow-x-auto sm:justify-center">
            <Pagination currentPage={state.page || 1} totalPages={Math.ceil(total / state.limit!)} onPageChange={onPageChange} />
        </div>}
    </div>
}

export default NotesPage;